import React, { useEffect, useMemo, useState } from 'react';
import {
  createWikiSummaryService,
  getWikiTranslationStatusMessage,
} from '../../lib/wikiSummaryService';

const WIKI_BASE_URL = 'https://backtothefuture.fandom.com';
const ALLORIGINS_URL = 'https://api.allorigins.win/raw?url=';
const JINA_PROXY_URL = 'https://r.jina.ai/http://';
const OFFICIAL_SITE_URL = 'https://www.backtothefuture.com/';
const DEFAULT_SEARCH = 'Marty McFly';

const WIKI_SECTION_CONFIG = [
  {
    label: 'Resumo',
    matches: ['plot summary', 'story', 'summary', 'synopsis', 'overview', 'premise'],
    maxParagraphs: 2,
  },
  {
    label: 'Biografia',
    matches: ['biography', 'history', 'background'],
    maxParagraphs: 2,
  },
  {
    label: 'Aparicoes',
    matches: ['appearances', 'appearances and sources', 'sources', 'media appearances'],
    maxParagraphs: 1,
  },
  {
    label: 'Poderes e habilidades',
    matches: ['powers and abilities', 'powers', 'abilities'],
    maxParagraphs: 1,
  },
  {
    label: 'Personalidade',
    matches: ['personality', 'traits'],
    maxParagraphs: 1,
  },
  {
    label: 'Aparencia',
    matches: ['appearance', 'physical appearance'],
    maxParagraphs: 1,
  },
  {
    label: 'Relacionamentos',
    matches: ['relationships'],
    maxParagraphs: 1,
  },
  {
    label: 'Curiosidades',
    matches: ['trivia'],
    maxParagraphs: 1,
  },
];

const extractJsonFromJinaResponse = (text) => {
  if (!text) {
    return null;
  }
  const marker = 'Markdown Content:';
  const index = text.indexOf(marker);
  if (index === -1) {
    return null;
  }
  const jsonText = text.slice(index + marker.length).trim();
  if (!jsonText) {
    return null;
  }
  try {
    return JSON.parse(jsonText);
  } catch (err) {
    return null;
  }
};

const fetchJsonWithFallback = async (url, signal) => {
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error('Wiki request failed.');
    }
    return await response.json();
  } catch (err) {
    const normalized = url.replace(/^https?:\/\//, '');
    const jinaUrl = `${JINA_PROXY_URL}${normalized}`;
    const proxyUrl = `${ALLORIGINS_URL}${encodeURIComponent(jinaUrl)}`;
    const response = await fetch(proxyUrl, { signal });
    if (!response.ok) {
      throw new Error('Wiki request failed.');
    }
    const text = await response.text();
    const json = extractJsonFromJinaResponse(text);
    if (!json) {
      throw new Error('Wiki request failed.');
    }
    return json;
  }
};

const wikiService = createWikiSummaryService({
  wikiBaseUrl: WIKI_BASE_URL,
  sectionConfig: WIKI_SECTION_CONFIG,
  maxTranslateLength: 1200,
  fetchJson: fetchJsonWithFallback,
});

const BackToTheFuture = () => {
  const [searchInput, setSearchInput] = useState(DEFAULT_SEARCH);
  const [searchTerm, setSearchTerm] = useState(DEFAULT_SEARCH);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [wikiData, setWikiData] = useState(null);

  useEffect(() => {
    if (!searchTerm) {
      return;
    }

    let active = true;
    const controller = new AbortController();

    const loadWiki = async () => {
      setStatus('loading');
      setError('');
      setWikiData(null);

      try {
        const result = await wikiService.loadWikiSummary(searchTerm, controller.signal);

        if (!active) {
          return;
        }

        if (!result) {
          setStatus('empty');
          return;
        }

        setWikiData(result);
        setStatus('success');
      } catch (err) {
        if (!active || err.name === 'AbortError') {
          return;
        }
        setStatus('error');
        setError('Nao foi possivel carregar a wiki agora.');
      }
    };

    loadWiki();

    return () => {
      active = false;
      controller.abort();
    };
  }, [searchTerm]);

  const wikiPageUrl = useMemo(() => {
    return wikiService.resolveWikiPageUrl(wikiData, searchTerm);
  }, [searchTerm, wikiData]);

  return (
    <>
      <section aria-labelledby="bttf-title" className="site-bg-hero text-center py-16 px-6">
        <h1 id="bttf-title" className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Back to the Future Wiki
        </h1>
        <p className="mt-6 text-lg leading-8 text-gray-600">
          Explore informacoes da Futurepedia (Fandom) com resumo traduzido e links oficiais.
        </p>
      </section>

      <div className="site-bg-section py-20 px-6">
        <div className="container mx-auto">
          <section aria-labelledby="bttf-official-title" className="mb-12">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-lg site-card max-w-4xl mx-auto">
              <h2 id="bttf-official-title" className="text-2xl font-semibold text-gray-900">
                Site oficial
              </h2>
              <p className="mt-2 text-gray-600">
                Noticias, eventos e informacoes oficiais do universo Back to the Future.
              </p>
              <div className="mt-4">
                <a
                  href={OFFICIAL_SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                >
                  Visitar site oficial
                </a>
              </div>
            </div>
          </section>

          <section aria-labelledby="bttf-search-title" className="mb-12">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-lg site-card max-w-4xl mx-auto">
              <h2 id="bttf-search-title" className="text-2xl font-semibold text-gray-900">
                Pesquisar na wiki
              </h2>
              <p className="mt-2 text-gray-600">
                Pesquise por personagens, lugares ou elementos da saga (ex.: Marty McFly, Doc Brown,
                DeLorean).
              </p>
              <form
                className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center"
                onSubmit={(event) => {
                  event.preventDefault();
                  setSearchTerm(searchInput.trim() || DEFAULT_SEARCH);
                }}
              >
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Digite um termo para pesquisar"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                  >
                    Buscar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput(DEFAULT_SEARCH);
                      setSearchTerm(DEFAULT_SEARCH);
                    }}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:border-blue-500 hover:text-blue-600"
                  >
                    Resetar
                  </button>
                </div>
              </form>
            </div>
          </section>

          <section aria-labelledby="bttf-results-title">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 id="bttf-results-title" className="text-2xl font-semibold text-gray-900">
                    Resultado da wiki
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Termo atual: <span className="font-semibold text-gray-900">{searchTerm}</span>
                  </p>
                </div>
              </div>

              {status === 'loading' && (
                <div className="mt-6 text-center text-gray-600">Carregando wiki...</div>
              )}
              {status === 'error' && <div className="mt-6 text-center text-red-600">{error}</div>}
              {status === 'empty' && (
                <div className="mt-6 text-center text-gray-600">Nenhuma pagina encontrada.</div>
              )}
              {status === 'success' && (
                <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-lg site-card p-6">
                  <div className="space-y-6 text-sm text-gray-600">
                    {(!wikiData?.sections || wikiData.sections.length === 0) && (
                      <p className="whitespace-pre-line">Resumo nao informado.</p>
                    )}
                    {(wikiData?.sections || []).map((section) => (
                      <div key={section.title || section.text?.slice(0, 20)}>
                        {section.title && (
                          <h4 className="text-base font-semibold text-gray-900">{section.title}</h4>
                        )}
                        <p className="mt-2 whitespace-pre-line">
                          {section.text || 'Resumo nao informado.'}
                        </p>
                      </div>
                    ))}
                    {wikiData?.sections?.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {getWikiTranslationStatusMessage(wikiData?.translationStatus)}
                      </p>
                    )}
                    {wikiPageUrl && (
                      <p className="text-xs text-gray-500">
                        Fonte:{' '}
                        <a
                          href={wikiPageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          {wikiData?.title || 'Futurepedia (Fandom)'}
                        </a>
                        . Conteudo sob CC BY-SA 3.0. Atribuicao e historico na pagina.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default BackToTheFuture;
